

































import { computed, defineComponent, ref } from "@vue/composition-api";
import { PayeeInformation } from "@scrile/api-provider/dist/api/PayeeInformationProvider";
import { PayoutRequestSettings } from "@scrile/api-provider/dist/api/PayoutRequestsProvider";
import { CREDITS_CURRENCY_CODE } from "@scrile/tools/dist/lib/MoneyHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { handleRequestError } from "@/lib/handleRequestError";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString, getCurrencySymbol } from "@/lib/moneyHelpers";
import BaseButton from "@/components/BaseButton";
import CreatePayoutRequestModal from "./CreatePayoutRequestModal";
import PayoutPayeeInfoModal from "./PayoutPayeeInfoModal";

const FIELD_AMOUNT_ERROR = "{javax.validation.constraints.Min.message}";
const NOT_ENOUGH_FUNDS_ERROR = "{com.scrile.money.validation.CreatePayoutRequestMinimumAmountValidator.message}";

export default defineComponent({
  name: "AddNewRequest",
  components: { PayoutPayeeInfoModal, BaseButton, CreatePayoutRequestModal },
  props: {
    isEmptyRequests: { type: Boolean, default: false },
    primaryBalance: { type: Number, required: true },
  },
  setup(props, { emit }) {
    const { authUserStrict } = useAuthUser();
    const showRequestModal = ref(false);
    const showPayeeInfoModal = ref(false);
    const forceAllowPayout = ref(false);
    const loading = ref(false);
    const inputValue = ref("");
    const currencySymbol = $t(getCurrencySymbol(CREDITS_CURRENCY_CODE));
    const requestErrorValue = ref("");

    const payeeInformation = ref<PayeeInformation | null>(null);
    const requestSettings = ref<PayoutRequestSettings | null>(null);

    const allowSend = computed(() => {
      return inputValue.value.length !== 0 && +inputValue.value > 0;
    });

    const allowRequest = computed(() => {
      return payeeInformation.value?.taxFormVerified && !!requestSettings.value;
    });

    const showPayoutRequestModal = computed(
      () => (showRequestModal.value && allowRequest.value) || forceAllowPayout.value
    );

    const subText = computed(() => {
      if (requestErrorValue.value) {
        return $t(requestErrorValue.value);
      }
      return $t("Min: {0}", [formatMoneyString(requestSettings.value?.minimumAmount ?? 0)]);
    });

    const onRequestButtonClick = () => {
      if (allowRequest.value) {
        showRequestModal.value = true;
      } else {
        showPayeeInfoModal.value = true;
      }
    };

    const onCreateRequest = async () => {
      if (!allowSend.value) return;
      try {
        loading.value = true;
        providers.PayoutRequestsProvider.setReceiveErrors(false);
        const r = await providers.PayoutRequestsProvider.create({
          data: {
            amount: +inputValue.value,
            userId: authUserStrict.value.id,
          },
        });
        inputValue.value = "";
        emit("createRequest", r);
      } catch (e) {
        handleRequestError(
          e,
          e =>
            e.details?.messageTemplate === FIELD_AMOUNT_ERROR || e.details?.messageTemplate === NOT_ENOUGH_FUNDS_ERROR,
          e => (requestErrorValue.value = e.message ?? "Error")
        );
      } finally {
        loading.value = false;
      }
    };

    const onCloseModal = () => {
      inputValue.value = "";
      showRequestModal.value = false;
      forceAllowPayout.value = false;
      requestErrorValue.value = "";
    };

    const onAvailableAmount = () => {
      inputValue.value = props.primaryBalance.toString();
      requestErrorValue.value = "";
    };

    const onInput = (v: string) => {
      const value = +v;
      requestErrorValue.value = "";
      if (isNaN(value) || value === 0) {
        inputValue.value = "";
      } else {
        inputValue.value = value.toString();
      }
    };

    const onPayeeInfoFilled = () => {
      forceAllowPayout.value = true;
      showPayeeInfoModal.value = false;
    };

    const getPayeeInfo = async () => {
      payeeInformation.value = await providers.PayeeInformationProvider.find({ id: authUserStrict.value.id });
    };
    const getOwnRequestSettings = async () => {
      const result = await providers.PayoutRequestsProvider.getSettings({
        data: { userIds: [authUserStrict.value.id] },
      });
      requestSettings.value = result.find(i => i.userId === authUserStrict.value.id) ?? null;
    };

    const getData = async () => {
      await Promise.all([getOwnRequestSettings(), getPayeeInfo()]);
    };

    getData();

    return {
      loading,
      showRequestModal,
      showPayoutRequestModal,
      showPayeeInfoModal,
      inputValue,
      currencySymbol,
      requestSettings,
      subText,
      requestErrorValue,
      forceAllowPayout,
      payeeInformation,
      allowRequest,
      onPayeeInfoFilled,
      onCreateRequest,
      onCloseModal,
      onAvailableAmount,
      onInput,
      onRequestButtonClick,
    };
  },
});
